import React, { useState, useCallback, useEffect } from 'react'
import {
  DialogRoot as Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Icon } from '@/components/ui/icon'
import { walletAPI } from '@/lib/api/wallet'
import { getStripe } from '@/lib/stripe'
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js'

// ============================================================================
// SETUP FORM (inside Elements provider)
// ============================================================================

interface SetupFormProps {
  onSuccess: () => void
  onCancel: () => void
}

function SetupForm({ onSuccess, onCancel }: SetupFormProps) {
  const stripe = useStripe()
  const elements = useElements()
  const [isProcessing, setIsProcessing] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      if (!stripe || !elements) return

      setIsProcessing(true)
      setError(null)

      try {
        const { error: submitError } = await elements.submit()
        if (submitError) {
          setError(submitError.message ?? 'Failed to add card')
          setIsProcessing(false)
          return
        }

        const { error: confirmError } = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}${window.location.pathname}?card=added`,
          },
          redirect: 'if_required',
        })

        if (confirmError) {
          setError(confirmError.message ?? 'Failed to add card')
          setIsProcessing(false)
          return
        }

        // Card saved — notify listeners to refresh payment methods
        window.dispatchEvent(new Event('genie:payment-methods-changed'))
        onSuccess()
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Something went wrong')
        setIsProcessing(false)
      }
    },
    [stripe, elements, onSuccess]
  )

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      {/* Stripe PaymentElement */}
      <div className="min-h-[120px]">
        <PaymentElement onReady={() => setReady(true)} options={{ layout: 'tabs' }} />
      </div>

      {/* Error */}
      {error && <p className="text-sm text-destructive">{error}</p>}

      {/* Security notice */}
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <Icon name="shield-check" className="h-3.5 w-3.5" />
        <span>Your card details are securely handled by Stripe</span>
      </div>

      {/* Actions */}
      <div className="flex gap-2">
        <Button
          type="button"
          variant="outline"
          className="flex-1"
          onClick={onCancel}
          disabled={isProcessing}
        >
          Cancel
        </Button>
        <Button type="submit" className="flex-1" disabled={!stripe || !ready || isProcessing}>
          {isProcessing ? (
            <>
              <Icon name="loader-2" className="mr-1.5 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            'Save card'
          )}
        </Button>
      </div>
    </form>
  )
}

// ============================================================================
// MAIN DIALOG
// ============================================================================

interface AddCardDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onAdd?: () => void
}

export function AddCardDialog({ open, onOpenChange, onAdd }: AddCardDialogProps) {
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [stripePromise] = useState(() => getStripe())

  // Create a SetupIntent when the dialog opens
  useEffect(() => {
    if (!open) {
      setClientSecret(null)
      setError(null)
      setIsLoading(false)
      return
    }

    const createSetup = async () => {
      setIsLoading(true)
      setError(null)
      try {
        const stripe = await stripePromise
        if (!stripe) throw new Error('Payments are currently unavailable. Please try again later.')
        const result = await walletAPI.createSetupIntent(true)
        if (!result.clientSecret)
          throw new Error('Card setup could not be initialized. Please try again.')
        setClientSecret(result.clientSecret)
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to initialize card setup')
      } finally {
        setIsLoading(false)
      }
    }

    void createSetup()
  }, [open, stripePromise])

  const handleSuccess = useCallback(() => {
    onAdd?.()
    onOpenChange(false)
  }, [onAdd, onOpenChange])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>Add payment method</DialogTitle>
          <DialogDescription className="sr-only">
            Add a new payment method to your account
          </DialogDescription>
        </DialogHeader>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Icon name="loader-2" className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center gap-3 py-8">
            <p className="text-sm text-destructive">{error}</p>
            <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
              Close
            </Button>
          </div>
        ) : clientSecret && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              // See TopUpDialog: the iframe needs the font loaded into it.
              fonts: [
                {
                  cssSrc:
                    'https://fonts.googleapis.com/css2?family=Geist:wght@100..900&display=swap',
                },
              ],
              appearance: {
                theme: 'stripe',
                variables: {
                  borderRadius: '8px',
                  fontFamily: "'Geist', system-ui, sans-serif",
                },
              },
            }}
          >
            <SetupForm onSuccess={handleSuccess} onCancel={() => onOpenChange(false)} />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}
